/**
 * API Client
 * Centralized API communication with auth and tenant context
 */

const API_BASE = process.env.REACT_APP_API_URL || '';

// ============================================================================
// Types
// ============================================================================

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
}

interface RequestOptions extends RequestInit {
  skipAuth?: boolean;
}

// ============================================================================
// Core API Functions
// ============================================================================

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE) {
    this.baseUrl = baseUrl;
  }

  private getAuthHeader(): Record<string, string> {
    const token = localStorage.getItem('access_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  async request<T = any>(
    endpoint: string,
    options: RequestOptions = {}
  ): Promise<ApiResponse<T>> {
    const { skipAuth = false, ...fetchOptions } = options;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(!skipAuth ? this.getAuthHeader() : {}),
      ...(fetchOptions.headers as Record<string, string> || {}),
    };

    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...fetchOptions,
        headers,
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle 401 - redirect to login
        if (response.status === 401) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          window.location.href = '/login';
        }
        return { success: false, error: data.error || 'Request failed' };
      }

      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  async get<T = any>(endpoint: string, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  async post<T = any>(endpoint: string, body?: any, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async put<T = any>(endpoint: string, body?: any, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async delete<T = any>(endpoint: string, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }

  // File upload (multipart)
  async uploadFile(endpoint: string, file: File, additionalData?: Record<string, any>): Promise<ApiResponse> {
    const formData = new FormData();
    formData.append('file', file);

    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, typeof value === 'object' ? JSON.stringify(value) : value);
      });
    }

    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'POST',
        headers: this.getAuthHeader(),
        body: formData,
      });

      const data = await response.json();
      return response.ok ? { success: true, data } : { success: false, error: data.error };
    } catch (error) {
      return { success: false, error: 'Upload failed' };
    }
  }
}

// ============================================================================
// API Instance
// ============================================================================

export const api = new ApiClient();

// ============================================================================
// API Endpoints
// ============================================================================

// Auth
export const authApi = {
  login: (email: string, password: string, tenantSlug?: string) =>
    api.post('/api/auth/login', { email, password, tenant_slug: tenantSlug }, { skipAuth: true }),

  register: (data: { email: string; password: string; name: string; company_name: string }) =>
    api.post('/api/auth/register', data, { skipAuth: true }),

  refresh: (refreshToken: string) =>
    api.post('/api/auth/refresh', { refresh_token: refreshToken }, { skipAuth: true }),

  me: () => api.get('/api/auth/me'),

  switchTenant: (tenantId: number) =>
    api.post('/api/auth/switch-tenant', { tenant_id: tenantId }),
};

// Tenant
export const tenantApi = {
  getConfig: () => api.get('/api/tenant/config'),
  getBranding: () => api.get('/api/tenant/branding'),
  updateBranding: (branding: Record<string, any>) => api.put('/api/tenant/branding', branding),
};

// Projects
export const projectApi = {
  list: (params?: { status?: string; client_id?: number }) => {
    const query = params ? '?' + new URLSearchParams(params as any).toString() : '';
    return api.get(`/api/projects${query}`);
  },

  get: (id: number) => api.get(`/api/projects/${id}`),

  create: (data: Record<string, any>) => api.post('/api/projects', data),

  update: (id: number, data: Record<string, any>) => api.put(`/api/projects/${id}`, data),

  uploadDocument: (projectId: number, file: File, documentType: string, stepNumber: number) =>
    api.uploadFile(`/api/projects/${projectId}/documents`, file, {
      document_type: documentType,
      step_number: stepNumber,
    }),

  getDocuments: (projectId: number) => api.get(`/api/projects/${projectId}/documents`),

  assign: (projectId: number, teamMemberId: number) =>
    api.post(`/api/projects/${projectId}/assign`, { team_member_id: teamMemberId }),
};

// Clients
export const clientApi = {
  list: () => api.get('/api/clients'),
  get: (id: number) => api.get(`/api/clients/${id}`),
  create: (data: Record<string, any>) => api.post('/api/clients', data),
  update: (id: number, data: Record<string, any>) => api.put(`/api/clients/${id}`, data),
};

// Team
export const teamApi = {
  list: (type?: '1099' | 'w2') => {
    const query = type ? `?type=${type}` : '';
    return api.get(`/api/team${query}`);
  },

  get: (id: number) => api.get(`/api/team/${id}`),

  add: (data: Record<string, any>) => api.post('/api/team', data),

  update: (id: number, data: Record<string, any>) => api.put(`/api/team/${id}`, data),

  checkin: (id: number, data: Record<string, any>) => api.post(`/api/team/${id}/checkin`, data),

  logHours: (id: number, data: Record<string, any>) => api.post(`/api/team/${id}/hours`, data),

  getHours: (id: number, period?: string) => {
    const query = period ? `?period=${period}` : '';
    return api.get(`/api/team/${id}/hours${query}`);
  },
};

// Check-ins
export const checkinApi = {
  getToday: () => api.get('/api/checkins/today'),
  sendReminders: () => api.post('/api/checkins/send-reminders'),
};

// Invoices
export const invoiceApi = {
  list: (status?: string) => {
    const query = status ? `?status=${status}` : '';
    return api.get(`/api/invoices${query}`);
  },

  get: (id: number) => api.get(`/api/invoices/${id}`),

  create: (data: Record<string, any>) => api.post('/api/invoices', data),

  send: (id: number) => api.post(`/api/invoices/${id}/send`),

  recordPayment: (id: number, data: Record<string, any>) =>
    api.post(`/api/invoices/${id}/payments`, data),
};

// Payments
export const paymentApi = {
  list: () => api.get('/api/payments'),
  record: (data: Record<string, any>) => api.post('/api/payments', data),
};

// Payroll
export const payrollApi = {
  getSummary: () => api.get('/api/payroll/summary'),
  run: () => api.post('/api/payroll/run'),
  getHistory: () => api.get('/api/payroll/history'),
};

// Taxes
export const taxApi = {
  getSummary: () => api.get('/api/taxes/summary'),
  get1099: (contractorId: number, year: number) =>
    api.get(`/api/contractor/${contractorId}/1099?year=${year}`),
};

// Rewards
export const rewardApi = {
  getCatalog: () => api.get('/api/rewards/catalog'),
  getLeaderboard: () => api.get('/api/rewards/leaderboard'),
  award: (memberId: number, points: number, reason: string) =>
    api.post('/api/rewards/points', { member_id: memberId, points, reason }),
  redeem: (memberId: number, rewardId: string) =>
    api.post('/api/rewards/redeem', { member_id: memberId, reward_id: rewardId }),
  getPending: () => api.get('/api/rewards/pending'),
  fulfill: (redemptionId: number) => api.post(`/api/rewards/${redemptionId}/fulfill`),
};

// Standards
export const standardsApi = {
  getNaming: () => api.get('/api/standards/naming'),
  getLayers: () => api.get('/api/standards/layers'),
  getTemplates: () => api.get('/api/standards/templates'),
};

export default api;
